import useLocalStorage from 'core/hooks/utils/useLocalStorage'
import { useMutation } from '@tanstack/react-query'
import { useQueryCallbacks } from 'core/hooks/data/useQueryCallbacks'
import APIService from 'api/APIService'
import { ID, JobStatus } from 'types/entity'
import { useSettings } from 'core/hooks/settings/useSettings'
import useNotifications from 'core/hooks/ui/useNotifications'
import { downloadFile } from 'utils/AppHelper'

interface PayPeriodDownloadProps {
  onSuccess?: VoidFunction
}

const usePayPeriodDownload = ({ onSuccess }: PayPeriodDownloadProps = {}) => {
  const { data: settings, set } = useSettings()
  const notification = useNotifications()

  const [localJobId, setJobId] = useLocalStorage(
    'pay_period_download_job_id',
    ''
  )
  const jobID = settings.pollingJobs.payPeriodDownloadJobId || localJobId

  useQueryCallbacks({
    queryKey: ['download-pay-period-polling', jobID],
    queryFn: () =>
      APIService.misc.getJobStatus({
        jobId: jobID
      }),
    enabled: !!jobID,
    refetchInterval: 2000,
    onSuccess: (response: JobStatus) => {
      if (response.status === 'finished' && !response.data?.errors) {
        notification.dismiss('payPeriodDownloadJobId')
        setJobId('')
        set('pollingJobs', {
          ...settings.pollingJobs,
          payPeriodDownloadJobId: ''
        })
        downloadFile(response.data.file_url as string)
        notification.success({
          title: 'Your download is complete.',
          id: `${jobID}-success`
        })
      }

      if (
        response.status === 'failed' ||
        (response.status === 'finished' && response.data?.errors)
      ) {
        notification.dismiss('payPeriodDownloadJobId')
        notification.error({
          title: 'Could not download pay period data at this time.',
          subtitle: (
            <div>
              {typeof response.data?.message === 'string' &&
                response.data.message?.split(';').map((error) => {
                  return <span key={error}>{error.replace(';', '')}</span>
                })}
            </div>
          ),
          id: jobID
        })
        setJobId('')
        set('pollingJobs', {
          ...settings.pollingJobs,
          payPeriodDownloadJobId: ''
        })
      }
    },
    onError: () => {
      notification.dismiss('payPeriodDownloadJobId')
      notification.error({
        title: 'Could not update pay period at this time',
        id: jobID
      })
      setJobId('')
      set('pollingJobs', {
        ...settings.pollingJobs,
        payPeriodDownloadJobId: ''
      })
    }
  })

  // Query to start pay period download - returns jobID
  const { mutate: downloadPayPeriod, status } = useMutation({
    mutationFn: ({
      id,
      includeDetails = false
    }: {
      id: ID
      includeDetails?: boolean
    }) => {
      return APIService.payPeriods.downloadPayPeriod({
        pay_period_id: id,
        include_transactions: includeDetails,
        include_trips: includeDetails
      })
    },
    onSuccess: (data) => {
      const downloadJobId = data?.token_id.toString()
      notification.loading({
        title: 'Getting download ready...',
        subtitle:
          'Your download will be available soon, this might take a few minutes',
        position: 'bottom-left',
        showClose: false,
        duration: Infinity,
        id: 'payPeriodDownloadJobId'
      })
      setTimeout(() => {
        set('pollingJobs', {
          ...settings.pollingJobs,
          payPeriodDownloadJobId: downloadJobId
        })
      }, 0)
      setJobId(downloadJobId)
      onSuccess?.()
    },
    onError: () => {
      notification.error({
        title: 'Could not download pay period data at this time.',
        id: 'payPeriodDownloadError'
      })
    }
  })
  return {
    downloadPayPeriod,
    status
  }
}

export default usePayPeriodDownload
